import { useTranslation } from "react-i18next";
import { Button, Icon } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import type { FlashCardUI } from "../models/FlashcardUI";
import styles from "./FlashcardOutlineSidebar.module.scss";

type FlashcardOutlineSidebarProps = {
  flashcards: FlashCardUI[];
  currentIndex: number;
  hasRevisionQuestions: boolean;
  onSelectCard: (index: number) => void;
  onSelectRevision: () => void;
  subjectName: string;
};

export function FlashcardOutlineSidebar({
  flashcards,
  currentIndex,
  hasRevisionQuestions,
  onSelectCard,
  onSelectRevision,
  subjectName,
}: FlashcardOutlineSidebarProps) {
  const { t } = useTranslation("selflearn");
  const { theme } = useTheme();
  const subjectColor =
    theme.colors.subject[subjectName] ?? theme.colors.primary;
  return (
    <nav
      className={styles.panel}
      aria-label={t("flashcards.outline.navAriaLabel")}
    >
      {flashcards.map((card, index) => {
        const done = card.isCompleted;
        const active = index === currentIndex;
        const seq = String(index + 1).padStart(2, "0");
        return (
          <Button
            key={card.flashcardId ?? index}
            type="button"
            variant="ghost"
            shape="square"
            size="md"
            width="100%"
            height="auto"
            className={`${styles.row} ${active ? styles.rowActive : ""}`}
            backgroundColor={
              active ? theme.colors.primary[90] : theme.colors.neutral[100]
            }
            onPress={() => onSelectCard(index)}
            accessibilityState={{ selected: active }}
            leftIcon={
              <div
                className={`${styles.badge} ${done ? styles.badgeDone : ""}`}
                style={{
                  backgroundColor: subjectColor[95],
                  color: subjectColor[80],
                }}
              >
                {done ? (
                  <Icon
                    name="checkmarkIcon"
                    size={32}
                    color={theme.colors.success[60]}
                  />
                ) : (
                  seq
                )}
              </div>
            }
          >
            <p className={styles.label}>
              {card.title ??
                t("flashcards.outline.cardNumber", { number: index + 1 })}
            </p>
          </Button>
        );
      })}
      {hasRevisionQuestions ? (
        <Button
          type="button"
          variant="ghost"
          shape="square"
          size="md"
          width="100%"
          height="auto"
          className={styles.row}
          backgroundColor={theme.colors.neutral[100]}
          onPress={onSelectRevision}
          leftIcon={
            <div
              className={styles.badge}
              style={{
                backgroundColor: subjectColor[95],
                color: subjectColor[80],
              }}
            >
              {String(flashcards.length + 1).padStart(2, "0")}
            </div>
          }
        >
          <p className={styles.label}>
            {t("flashcards.outline.revisionQuestions")}
          </p>
        </Button>
      ) : null}
    </nav>
  );
}
